import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import compose from 'recompose/compose';
import Image from '@spothero/ui/image';
import TextButton from '@spothero/ui/text-button';
import EnvironmentUtils from '@spothero/utils/environment';
import {
    showChangeDateTime,
    toggleTimeExtendedDetails,
} from 'checkout/checkout-actions';
import {setTerm} from 'spot/spot-actions';
import CheckoutUtils from 'checkout/checkout-utils';
import {SPOT_IMAGES} from 'spot-details/spot-details-prop-types';
import Disclaimers from './Disclaimers';
import ReviewPurchase from './ReviewPurchase';
import Card from 'common/card/Card';
import WithConfig from 'config/WithConfig';
import CloudinaryImage from 'common/image/CloudinaryImage';

class ReservationSummary extends Component {
    static propTypes = {
        starts: PropTypes.string.isRequired,
        ends: PropTypes.string,
        isAirport: PropTypes.bool.isRequired,
        isMonthly: PropTypes.bool.isRequired,
        selectedRate: PropTypes.object.isRequired,
        hourlyRates: PropTypes.arrayOf(PropTypes.object).isRequired,
        monthlyRates: PropTypes.arrayOf(PropTypes.object).isRequired,
        onChangeDateTimeClick: PropTypes.func.isRequired,
        onTimeExtendedClick: PropTypes.func.isRequired,
        facilityTitle: PropTypes.string.isRequired,
        spotImages: SPOT_IMAGES,
        onReservationTermClick: PropTypes.func.isRequired,
        staticBaseUrl: PropTypes.string,
        isCollapsedSummary: PropTypes.bool
    };

    constructor(props) {
        super(props);

        this._isMobile = EnvironmentUtils.isMobileDevice();
    }

    _onReservationTermClick = () => {
        const {
            isMonthly,
            monthlyRates,
            hourlyRates,
            onReservationTermClick
        } = this.props;
        const newRate = (!isMonthly)
            ? monthlyRates[0] || null
            : hourlyRates[0] || null;

        onReservationTermClick({
            newRate,
            rid: (newRate) ? newRate.rule_id : null,
            monthly: (!isMonthly)
        });
    }

    _getTransientTimes = () => {
        const {
            starts,
            ends,
            selectedRate
        } = this.props;
        const {
            starts: transientStarts,
            ends: transientEnds
        } = selectedRate;

        return CheckoutUtils.getTransientReservationInfo({
            reservationStarts: starts,
            reservationEnds: ends,
            rateStarts: transientStarts,
            rateEnds: transientEnds,
            format: 'ddd, MMM D, h:mm A'
        });
    }

    _getMonthlyTimes = () => {
        const {
            selectedRate,
            starts
        } = this.props;
        const {
            startDateFormatted: startDateTimeFormatted,
            endDateFormatted: endDateTimeFormatted
        } = CheckoutUtils.getMonthlyReservationInfo(selectedRate, starts);

        return {
            startDateTimeFormatted,
            endDateTimeFormatted,
            timeExtended: false
        };
    }

    render() {
        const {
            isAirport,
            isMonthly,
            onChangeDateTimeClick,
            onTimeExtendedClick,
            hourlyRates,
            monthlyRates,
            facilityTitle,
            spotImages,
            staticBaseUrl,
            isCollapsedSummary
        } = this.props;
        const {
            startDateTimeFormatted,
            endDateTimeFormatted,
            timeExtended
        } = (isMonthly)
            ? this._getMonthlyTimes()
            : this._getTransientTimes();
        const labels = {
            monthly: ['Month Begins', 'Month Ends'],
            transient: ['Enter After', 'Exit Before'],
            airport: ['Parking Begins', 'Parking Ends']
        };
        const labelKey = (isAirport)
            ? 'airport'
            : (isMonthly)
                ? 'monthly'
                : 'transient';
        const [
            startsLabel,
            endsLabel
        ] = labels[labelKey];
        const showMobileCollapsedSummary = (this._isMobile && isCollapsedSummary);

        return (
            <div className="ReservationSummary-container">
                <div className="ReservationSummary">
                    {(!showMobileCollapsedSummary) &&
                        <Card className="ReservationSummary-facility-summary">
                            <div className="ReservationSummary-facility-summary-left">
                                {(spotImages.length > 0)
                                    ? (
                                        <CloudinaryImage
                                            className="ReservationSummary-facility-thumbnail-image"
                                            cloudinaryImageId={spotImages[0].id}
                                            width={64}
                                            height={64}
                                            alt={`Photo of ${facilityTitle}`}
                                        />
                                    )
                                    : (
                                        <Image
                                            className="ReservationSummary-facility-thumbnail-image"
                                            src={`${staticBaseUrl}img/missing-image.png`}
                                            alt={`Photo of ${facilityTitle}`}
                                        />
                                    )
                                }
                            </div>
                            <div className="ReservationSummary-facility-summary-right">
                                <span className="ReservationSummary-facility-title">{facilityTitle}</span>
                            </div>
                        </Card>
                    }
                    <Card
                        title={
                            <Fragment>
                                <h2>Reservation Summary</h2>
                                <TextButton
                                    className="ReservationSummary-edit-times"
                                    onClick={onChangeDateTimeClick}
                                >
                                    Edit
                                </TextButton>
                            </Fragment>
                        }
                    >
                        {(showMobileCollapsedSummary)
                            ? (
                                <div className="ReservationSummary-collapsed">
                                    <div className="ReservationSummary-collapsed-left">
                                        {(spotImages.length > 0)
                                            ? (
                                                <CloudinaryImage
                                                    className="ReservationSummary-collapsed-thumbnail"
                                                    cloudinaryImageId={spotImages[0].id}
                                                    width={64}
                                                    height={64}
                                                    alt={`Photo of ${facilityTitle}`}
                                                />
                                            )
                                            : (
                                                <Image
                                                    className="ReservationSummary-collapsed-thumbnail"
                                                    src={`${staticBaseUrl}img/missing-image.png`}
                                                    alt={`Photo of ${facilityTitle}`}
                                                />
                                            )
                                        }
                                    </div>
                                    <div className="ReservationSummary-collapsed-right">
                                        <span className="ReservationSummary-collapsed-title">{facilityTitle}</span>
                                        {(isMonthly)
                                            ? (
                                                <span className="ReservationSummary-collapsed-time">
                                                    {startDateTimeFormatted} - {endDateTimeFormatted}
                                                </span>
                                            ) : (
                                                <Fragment>
                                                    <span className="ReservationSummary-collapsed-time">
                                                        {startDateTimeFormatted}
                                                    </span>
                                                    <span className="ReservationSummary-collapsed-time">
                                                        {endDateTimeFormatted}
                                                    </span>
                                                </Fragment>
                                            )
                                        }
                                    </div>
                                </div>
                            ) : (
                                <div className="ReservationSummary-datetimes">
                                    <p data-test="start-date-time">
                                        <span className="label">{startsLabel}</span>
                                        {startDateTimeFormatted}
                                        <span className="arrow">&#x02192;</span>
                                    </p>
                                    <p data-test="end-date-time">
                                        <span className="label">{endsLabel}</span>
                                        {endDateTimeFormatted}
                                    </p>
                                </div>
                            )
                        }
                        {((isMonthly && hourlyRates.length > 0) || (!isMonthly && monthlyRates.length > 0)) &&
                            <TextButton
                                className="toggle-monthly-parking"
                                onClick={this._onReservationTermClick}
                            >
                                View {(isMonthly) ? 'Hourly' : 'Monthly'} Parking Options
                            </TextButton>
                        }
                        {timeExtended &&
                            <TextButton
                                className="ReservationSummary-time-extended-toggle"
                                onClick={onTimeExtendedClick}
                            >
                                Why is my time extended for free?
                            </TextButton>
                        }
                        <ReviewPurchase />
                        <Disclaimers />
                    </Card>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        searchRequest: {
            monthly: isMonthly,
            airport: isAirport,
            starts,
            ends
        },
        spot: {
            selected: {
                selectedRate,
                hourly_rates: hourlyRates,
                monthly_rates: monthlyRates,
                facility: {
                    title: facilityTitle,
                    images: spotImages
                }
            }
        },
        user: {
            data: {
                isCollapsedSummary
            }
        }
    } = state;

    return {
        isAirport,
        isMonthly,
        selectedRate,
        starts,
        ends,
        facilityTitle,
        hourlyRates,
        monthlyRates,
        spotImages,
        isCollapsedSummary
    };
};

const mapDispatchToProps = {
    onChangeDateTimeClick: showChangeDateTime,
    onTimeExtendedClick: toggleTimeExtendedDetails,
    onReservationTermClick: setTerm,
};

const enhance = compose(
    WithConfig,
    connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(ReservationSummary);
